import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SiX } from 'react-icons/si';
import { Mail } from 'lucide-react';

// Footer logo (dark version for white background)
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_6a70c609-1eda-4f98-b761-03a1d94ae0b4/artifacts/3qxp6d1x_Group%2087.png';

// Footer links
const footerLinks = {
  product: [
    { name: 'Chains', href: '/chains' },
    { name: 'Plugins', href: '/plugins' },
    { name: 'enaibl', href: '/enaibl' },
  ],
  resources: [
    { name: 'Documentation', href: '/docs' },
    { name: 'API Reference', href: '/docs/api/endpoints' },
    { name: 'Quick Start', href: '/docs/quickstart' },
  ],
  company: [
    { name: 'Tokenomics', href: '/docs/tokenomics' },
    { name: 'Governance', href: '/docs/governance' },
    { name: 'Fees', href: '/docs/fees' },
  ],
};

export default function CTAFooter() {
  const [email, setEmail] = useState('');

  return (
    <section data-testid="cta-footer-section" className="relative">
      {/* Top dark area behind the card */}
      <div className="bg-[#0a0a0a] h-[280px]" />
      
      {/* White footer with rounded top */}
      <div 
        className="bg-white relative"
        style={{
          borderTopLeftRadius: '32px',
          borderTopRightRadius: '32px',
          marginTop: '-32px',
        }}
      >
        {/* Dark CTA Card - positioned to overlap */}
        <div 
          className="absolute left-1/2 -translate-x-1/2 w-full max-w-[1200px] px-6 md:px-12"
          style={{ top: '-320px' }}
        >
          <div 
            className="rounded-[40px] overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 25%, #1a1a1a 50%, #0f0f0f 100%)',
              boxShadow: '0 4px 60px rgba(255,255,255,0.03), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
          >
            {/* Dot pattern background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundSize: '24px 24px',
                backgroundImage: 'radial-gradient(#404040 1px, transparent 1px)',
              }}
            />
            {/* Radial fade overlay */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, transparent 20%, #1a1a1a 70%)',
              }}
            />
            
            {/* CTA Area */}
            <div className="relative z-10 px-8 md:px-16 py-20 md:py-28 text-center">
              <h2 
                className="text-[32px] md:text-[48px] font-bold tracking-[-0.02em] leading-[1.15] text-white mb-10"
                data-testid="cta-headline"
              >
                Plug your agent into DeFi.
              </h2>

              {/* Email + CTA row */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-[520px] mx-auto">
                {/* Email input */}
                <div 
                  className="flex items-center gap-3 w-full sm:flex-1 px-5 py-3.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm"
                >
                  <Mail size={18} className="text-white/40 flex-shrink-0" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-[14px] text-white placeholder:text-white/40"
                    data-testid="email-input"
                  />
                </div>
                
                {/* CTA button */}
                <button 
                  className="w-full sm:w-auto bg-white text-black font-semibold text-[14px] px-8 py-3.5 rounded-full transition-all duration-200 hover:bg-white/90 whitespace-nowrap"
                  data-testid="cta-btn"
                >
                  Get Early Access
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer content - with padding top to account for overlapping card */}
        <div className="pt-[160px] pb-6">
          <div className="max-w-[1100px] mx-auto px-6">
            {/* Top row: Logo & Links */}
            <div className="flex flex-col lg:flex-row justify-between gap-12 mb-16">
              {/* Left - Logo + Description + Socials */}
              <div className="lg:max-w-[340px]">
                <img 
                  src={LOGO_DARK} 
                  alt="aibl" 
                  className="h-7 object-contain mb-5"
                  data-testid="footer-logo"
                />
                <p className="text-[14px] text-gray-500 leading-[1.7] mb-6">
                  AIBL connects autonomous agents to on-chain liquidity, multi-chain payments, lending, and DeFi infrastructure across 20+ chains.
                </p>
                <div className="flex items-center gap-4">
                  <a href="#" className="text-gray-400 hover:text-gray-900 transition-colors" aria-label="X" data-testid="social-x">
                    <SiX size={18} />
                  </a>
                </div>
              </div>

              {/* Right - Link columns */}
              <div className="grid grid-cols-3 gap-12 lg:gap-16">
                {/* Product */}
                <div>
                  <h4 className="text-[13px] font-semibold text-gray-900 mb-4">Product</h4>
                  <ul className="space-y-3">
                    {footerLinks.product.map((link) => (
                      <li key={link.name}>
                        <Link 
                          to={link.href}
                          className="text-[14px] text-gray-500 hover:text-gray-900 transition-colors"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Resources */}
                <div>
                  <h4 className="text-[13px] font-semibold text-gray-900 mb-4">Resources</h4>
                  <ul className="space-y-3">
                    {footerLinks.resources.map((link) => (
                      <li key={link.name}>
                        <Link 
                          to={link.href}
                          className="text-[14px] text-gray-500 hover:text-gray-900 transition-colors"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Company */}
                <div>
                  <h4 className="text-[13px] font-semibold text-gray-900 mb-4">Protocol</h4>
                  <ul className="space-y-3">
                    {footerLinks.company.map((link) => (
                      <li key={link.name}>
                        <Link 
                          to={link.href}
                          className="text-[14px] text-gray-500 hover:text-gray-900 transition-colors"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom row: Copyright + Legal */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-100">
              <span className="text-[13px] text-gray-400">© 2026 AIBL. All rights reserved.</span>
              <div className="flex items-center gap-6">
                <Link to="/privacy" className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors underline">Privacy Policy</Link>
                <Link to="/terms" className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors underline">Terms of Service</Link>
              </div>
            </div>
          </div>

          {/* Large "AI Banking Layer" text at bottom */}
          <div className="w-full overflow-hidden mt-8">
            <h2 
              className="text-center font-black tracking-[-0.02em] leading-none select-none"
              style={{
                fontSize: 'clamp(32px, 10vw, 180px)',
                color: 'rgba(0,0,0,0.12)',
                fontFamily: "'RC Demo', system-ui, sans-serif",
                overflow: 'hidden',
              }}
              data-testid="footer-large-text"
            >
              AI Banking Layer
            </h2>
          </div>
        </div>
      </div>
    </section>
  );
}
